import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type DialogSummary,
  TELEGRAM_SERVICE_PEER_ID,
  useAutoInboxTick,
  useDialogs,
  useInboxTick,
  useMarkDialogRead,
} from "@/lib/inbox";
import { pushToast } from "@/components/Toaster";
import { FOLLOW_UP_DUE, type LeadFilterValue } from "./LeadStatus";
import { useArchiveWithUndo } from "./useArchiveWithUndo";

// How many dialogs the list asks for, and how many "show more" adds — up to
// what the server gives in one answer.
const PAGE = 100;
const MAX_ROWS = 1000;

/**
 * The dialog list both inbox screens stand on: its filters, which dialog is
 * open, and the actions on it. InboxPage and InboxDrawer keep only their own
 * layout — a filter or an action added here shows up in both.
 *
 * `active` is false while the surface is closed (the drawer stays mounted at
 * the shell): no dialog is selected and Telegram is not polled.
 *
 * Passive reading must NOT bump last_used_at — that rule lives server-side; a
 * dialog is marked read only when the operator picks it.
 */
export function useInboxList({
  active = true,
  initialStatus = null,
}: {
  active?: boolean;
  /** The status filter the list opens with — a link from elsewhere sets it. */
  initialStatus?: LeadFilterValue | null;
} = {}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [accountFilter, setAccountFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LeadFilterValue | null>(initialStatus);
  // The archive instead of the working list.
  const [archiveView, setArchiveView] = useState(false);
  // One campaign's answers: its id, or "none" for those no campaign started.
  const [campaignFilter, setCampaignFilter] = useState<string | null>(null);
  // The search box, and what of it has been asked for: the request waits for
  // a pause in the typing.
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const dialogs = useDialogs({
    unread_only: unreadOnly,
    ...(statusFilter === FOLLOW_UP_DUE
      ? { follow_up_due: true }
      : { lead_status: statusFilter ?? undefined }),
    q: q || undefined,
    archived: archiveView,
    campaign: campaignFilter ?? undefined,
    limit,
  });
  // "Show more" is for the list on screen. Closed, or under another filter, it
  // starts from one page again: a list left grown would be fetched whole on
  // every refresh — by the drawer too, which asks even while it is shut. (The
  // account filter narrows what is loaded, so it leaves the size alone.)
  useEffect(() => {
    setLimit(PAGE);
  }, [active, unreadOnly, statusFilter, archiveView, campaignFilter, q]);

  const tick = useInboxTick();
  const markRead = useMarkDialogRead();
  const archiveWithUndo = useArchiveWithUndo();

  // Live-poll Telegram on a cadence while the surface is open + visible, so new
  // replies surface without pressing "Refresh now".
  useAutoInboxTick(active);

  // The accounts met in any list this session — the options of the account
  // switcher. They must not follow the list on screen: an unread or a status
  // filter can leave the chosen account without a row, and it would drop out
  // of its own menu.
  const [accounts, setAccounts] = useState<ReadonlyMap<string, string | null>>(new Map());
  useEffect(() => {
    const rows = dialogs.data;
    if (!rows) return;
    setAccounts((seen) => {
      if (rows.every((d) => seen.get(d.account_id) === d.account_label)) return seen;
      const next = new Map(seen);
      for (const d of rows) next.set(d.account_id, d.account_label);
      return next;
    });
  }, [dialogs.data]);

  // Dialogs scoped to the chosen account — client-side, the fetch is shared.
  const visible = useMemo(() => {
    if (!dialogs.data) return undefined;
    if (accountFilter) return dialogs.data.filter((d) => d.account_id === accountFilter);
    // "Все аккаунты": скрываем сервисный Telegram (777000 — коды входа), это шум
    // в общем списке. При выборе конкретного аккаунта его коды снова видны.
    return dialogs.data.filter((d) => d.peer_tg_id !== TELEGRAM_SERVICE_PEER_ID);
  }, [dialogs.data, accountFilter]);

  // Keep a valid selection within the current filter: auto-select the first
  // dialog when nothing valid is selected, keep a still-present selection across
  // 30s refetches (no re-select churn), clear it when the list empties or the
  // surface closes.
  useEffect(() => {
    if (!active || !visible || visible.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((cur) => (cur && visible.some((d) => d.id === cur) ? cur : visible[0].id));
  }, [active, visible]);

  const selected = useMemo(
    () => visible?.find((d) => d.id === selectedId) ?? null,
    [visible, selectedId],
  );

  const select = (dialog: DialogSummary) => {
    setSelectedId(dialog.id);
    if (dialog.unread_count > 0) markRead.mutate(dialog.id);
  };

  /**
   * Archive a dialog — or, from the archive, bring it back — with the undo
   * toast. Either way it leaves the list on screen, and the selection with it.
   */
  const archive = (dialog: DialogSummary, onArchived?: () => void) =>
    archiveWithUndo(dialog.id, {
      unarchive: dialog.archived,
      onArchived: () => {
        setSelectedId((cur) => (cur === dialog.id ? null : cur));
        onArchived?.();
      },
    });
  const archiveSelected = (onArchived?: () => void) => {
    if (selected) archive(selected, onArchived);
  };

  const refresh = () => {
    tick.mutate(undefined, {
      // "0 new" is only good news if the accounts were actually reached.
      onSuccess: (res) =>
        pushToast(
          res.errors > 0
            ? {
                kind: "warning",
                text: t("inbox.refresh_partial", { count: res.new_messages, errors: res.errors }),
              }
            : { kind: "success", text: t("inbox.refresh_result", { count: res.new_messages }) },
        ),
      onError: () => pushToast({ kind: "error", text: t("inbox.refresh_failed") }),
    });
  };

  return {
    accounts,
    isLoading: dialogs.isLoading,
    /** What the filters leave: the rows of the list. */
    visible,
    selected,
    select,
    archive,
    archiveSelected,
    archiveView,
    setArchiveView,
    unreadOnly,
    setUnreadOnly,
    accountFilter,
    setAccountFilter,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    campaignFilter,
    setCampaignFilter,
    /** Is the list narrowed by anything — an empty one then means "no match". */
    filtered:
      unreadOnly ||
      accountFilter !== null ||
      statusFilter !== null ||
      campaignFilter !== null ||
      q !== "",
    resetFilters: () => {
      setUnreadOnly(false);
      setAccountFilter(null);
      setStatusFilter(null);
      setCampaignFilter(null);
      setSearch("");
    },
    /** The server had a full page to give: there may be older dialogs. */
    hasMore: (dialogs.data?.length ?? 0) >= limit && limit < MAX_ROWS,
    showMore: () => setLimit((n) => Math.min(n + PAGE, MAX_ROWS)),
    refresh,
    refreshing: tick.isPending,
  };
}

export type InboxList = ReturnType<typeof useInboxList>;
